var _ = require("../../../uam/module.js")(module.exports, {
  namespace: "ua.access",
  title: "Access Policy Control",
  description: "Unified access and privacy control for pages, files, and shared resources.",
  control: true,
  preview: "ua.preview.access",
  supportServer: false,
  fields: {
    name: { title: "Name", type: "string", description: "Control name.", required: true },
    title: { title: "Title", type: "string", description: "Label shown above the control.", default: "Access" },
    policy: { title: "Policy", type: "string", description: "Initial access policy.", default: "public" },
    role: { title: "Role", type: "string", description: "Alias of requiredRole for role-based access." },
    requiredRole: { title: "Required Role", type: "string", description: "Default required role for role-based access.", default: "editor" },
    accessRoles: { title: "Access Roles", type: "array", of: "string", description: "Role allow-list. Any matching role grants access.", default: [] },
    emails: { title: "Emails", type: "array", of: "string", description: "Default allowed emails for email-based access.", default: [] },
    secureLinkExpiry: { title: "Secure Link Expiry", type: "string", description: "Default secure-link expiry option.", default: "none" },
    maxSecureLinkExpiry: { title: "Max Secure Link Expiry", type: "string", description: "Maximum selectable secure-link expiry.", default: "none" }
  }
});

var EMAIL_REGEX = /[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}/;

var POLICY_PUBLIC = "public";
var POLICY_SHARED_SECRET = "shared-secret";
var POLICY_LOGGED_IN = "logged-in";
var POLICY_ROLE = "role";
var POLICY_EMAIL = "email";
var POLICY_SECURE_LINK = "secure-link";

var SECURE_LINK_24H = "24h";
var SECURE_LINK_7D = "7d";
var SECURE_LINK_30D = "30d";
var SECURE_LINK_NONE = "none";
var SECURE_LINK_EXPIRIES = [SECURE_LINK_24H, SECURE_LINK_7D, SECURE_LINK_30D, SECURE_LINK_NONE];

function safe(value) {
  return (value || "").toString().trim();
}

function lower(value) {
  return safe(value).toLowerCase();
}

function normalizePolicy(value) {
  var policy = lower(value);
  if (!(policy)) return POLICY_PUBLIC;
  if (policy === POLICY_PUBLIC || policy === POLICY_SHARED_SECRET || policy === POLICY_LOGGED_IN || policy === POLICY_ROLE || policy === POLICY_EMAIL || policy === POLICY_SECURE_LINK) {
    return policy;
  }
  return POLICY_PUBLIC;
}

function normalizeSecureLinkExpiry(value) {
  var expiry = lower(value);
  if (expiry === "unlimited" || expiry === "no-expiration" || expiry === "never") return SECURE_LINK_NONE;
  if (SECURE_LINK_EXPIRIES.indexOf(expiry) >= 0) return expiry;
  return SECURE_LINK_NONE;
}

function secureLinkExpiryRank(value) {
  var normalized = normalizeSecureLinkExpiry(value);
  if (normalized === SECURE_LINK_24H) return 1;
  if (normalized === SECURE_LINK_7D) return 2;
  if (normalized === SECURE_LINK_30D) return 3;
  return 4;
}

function allowedSecureLinkExpiries(maxSecureLinkExpiry) {
  var maxExpiry = normalizeSecureLinkExpiry(maxSecureLinkExpiry || SECURE_LINK_NONE);
  var maxRank = secureLinkExpiryRank(maxExpiry);
  var allowed = [];
  for (var i = 0; i < SECURE_LINK_EXPIRIES.length; i++) {
    if (secureLinkExpiryRank(SECURE_LINK_EXPIRIES[i]) <= maxRank) {
      allowed.push(SECURE_LINK_EXPIRIES[i]);
    }
  }
  return allowed;
}

function clampSecureLinkExpiry(value, maxSecureLinkExpiry) {
  var expiry = normalizeSecureLinkExpiry(value);
  var allowed = allowedSecureLinkExpiries(maxSecureLinkExpiry);
  if (allowed.indexOf(expiry) >= 0) return expiry;
  if (allowed.length > 0) return allowed[allowed.length - 1];
  return SECURE_LINK_24H;
}

function policyOptions() {
  return [
    { policy: POLICY_PUBLIC, icon: "bi-globe2", text: "Anyone can view.", title: "Public" },
    { policy: POLICY_SHARED_SECRET, icon: "bi-lock-fill", text: "Shared secret required.", title: "Shared Secret" },
    { policy: POLICY_LOGGED_IN, icon: "bi-person-fill", text: "Any logged-in user.", title: "Logged In" },
    { policy: POLICY_ROLE, icon: "bi-person-lock", text: "Logged-in user with role.", title: "Role" },
    { policy: POLICY_EMAIL, icon: "bi-envelope-fill", text: "Only listed emails.", title: "Email" },
    { policy: POLICY_SECURE_LINK, icon: "bi-link-45deg", text: "URL token based access.", title: "Secure Link" }
  ];
}

function findOption(options, policy) {
  var activePolicy = normalizePolicy(policy);
  for (var i = 0; i < options.length; i++) {
    if (options[i].policy === activePolicy) return options[i];
  }
  return options[0];
}

function normalizeEmails(value) {
  var rows = [];
  if (value instanceof Array) rows = value.slice();
  else if (typeof value === "string") rows = value.split(/[\n\r,;]+/g);
  else if (value !== undefined && value !== null) rows = [value];

  var next = [];
  var seen = {};
  for (var i = 0; i < rows.length; i++) {
    var email = lower(rows[i]);
    if (!(email) || !(EMAIL_REGEX.test(email)) || seen[email]) continue;
    seen[email] = true;
    next.push(email);
  }
  return next;
}

function normalizeRoles(value) {
  var rows = [];
  if (value instanceof Array) rows = value.slice();
  else if (typeof value === "string") rows = value.split(/[\n\r,;]+/g);
  else if (value !== undefined && value !== null) rows = [value];

  var next = [];
  var seen = {};
  for (var i = 0; i < rows.length; i++) {
    var role = lower(rows[i]).replace(/[^a-z0-9._-]/g, "");
    if (!(role) || seen[role]) continue;
    seen[role] = true;
    next.push(role);
  }
  return next;
}

function createChip(host, email) {
  var value = lower(email);
  if (!(value) || !(EMAIL_REGEX.test(value))) return;
  var existing = host.querySelectorAll("[data-access-email]");
  for (var e = 0; e < existing.length; e++) {
    if (lower(existing[e].getAttribute("data-access-email")) === value) return;
  }

  var chip = document.createElement("span");
  chip.className = "ua-access-email-chip";
  chip.setAttribute("data-access-email", value);
  chip.innerHTML = '<span class="ua-access-email-chip-text"></span><button type="button" class="ua-access-email-chip-remove" aria-label="Remove ' + value.replace(/"/g, "&quot;") + '">&times;</button>';
  chip.querySelector(".ua-access-email-chip-text").textContent = value;

  chip.querySelector(".ua-access-email-chip-remove").addEventListener("click", function() {
    if (chip.parentNode) chip.parentNode.removeChild(chip);
  });

  host.appendChild(chip);
}

function createRoleChip(host, role) {
  var value = lower(role).replace(/[^a-z0-9._-]/g, "");
  if (!(value)) return;
  var existing = host.querySelectorAll("[data-access-role]");
  for (var e = 0; e < existing.length; e++) {
    if (lower(existing[e].getAttribute("data-access-role")) === value) return;
  }

  var chip = document.createElement("span");
  chip.className = "ua-access-email-chip";
  chip.setAttribute("data-access-role", value);
  chip.innerHTML = '<span class="ua-access-email-chip-text"></span><button type="button" class="ua-access-email-chip-remove" aria-label="Remove ' + value.replace(/"/g, "&quot;") + '">&times;</button>';
  chip.querySelector(".ua-access-email-chip-text").textContent = value;
  chip.querySelector(".ua-access-email-chip-remove").addEventListener("click", function() {
    if (chip.parentNode) chip.parentNode.removeChild(chip);
  });
  host.appendChild(chip);
}

function readEmails(host) {
  var chips = host.querySelectorAll("[data-access-email]");
  var rows = [];
  for (var i = 0; i < chips.length; i++) {
    rows.push(lower(chips[i].getAttribute("data-access-email")));
  }
  return normalizeEmails(rows);
}

function readRoles(host) {
  var chips = host.querySelectorAll("[data-access-role]");
  var rows = [];
  for (var i = 0; i < chips.length; i++) {
    rows.push(lower(chips[i].getAttribute("data-access-role")));
  }
  return normalizeRoles(rows);
}

async function loadRoleOptions(wrapper) {
  var datalist = wrapper.querySelector("[data-access-role-options]");
  if (!(datalist)) return [];

  var baseRoles = ["editor", "admin.app", "superadmin.universe"];
  var roleSet = {};
  for (var i = 0; i < baseRoles.length; i++) roleSet[baseRoles[i]] = true;

  try {
    var repo = await fetch("/auth/role-definition/list");
    var json = await repo.json();
    var rows = (json && json.success && json.data && json.data.list) ? json.data.list : [];
    for (var r = 0; r < rows.length; r++) {
      var ns = lower((rows[r] || {}).namespace);
      if (ns) roleSet[ns] = true;
    }
  } catch (error) {
    console.warn("ua.access: role definition list load failed.", error);
  }

  var roles = Object.keys(roleSet).sort(function(a, b) { return a.localeCompare(b); });
  datalist.innerHTML = "";
  for (var k = 0; k < roles.length; k++) {
    var option = document.createElement("option");
    option.value = roles[k];
    datalist.appendChild(option);
  }
  return roles;
}

function buildMarkup(options) {
  var policy = normalizePolicy(options.policy || options.type);
  var rows = policyOptions();
  var active = findOption(rows, policy);
  var label = safe(options.title || "Access");

  var wrapper = document.createElement("div");
  wrapper.className = "form-group pb-3 ua-access-control";
  wrapper.setAttribute("name", options.name || "");
  wrapper.setAttribute("data-access-policy", active.policy);
  wrapper.setAttribute("data-access-pending-roles", normalizeRoles((options && options.accessRoles) || [options.role || options.requiredRole]).join(","));
  wrapper.setAttribute("data-access-max-secure-link-expiry", normalizeSecureLinkExpiry(options.maxSecureLinkExpiry || SECURE_LINK_NONE));
  wrapper.setAttribute("data-access-secure-link-expiry", clampSecureLinkExpiry(options.secureLinkExpiry || options.secureLinkExpiresIn, options.maxSecureLinkExpiry || SECURE_LINK_NONE));
  wrapper.innerHTML = [
    '<label class="form-label fw-semibold">' + label + "</label>",
    '<div class="ua-access-line d-flex flex-wrap align-items-center gap-2">',
    '<div class="btn-group" role="group">',
    '<button type="button" class="btn btn-outline-secondary ua-access-toggle" data-access-toggle="cycle"><i class="bi ' + active.icon + ' me-1"></i><span class="ua-access-toggle-label">' + active.title + "</span></button>",
    '<button type="button" class="btn btn-outline-secondary dropdown-toggle dropdown-toggle-split ua-access-toggle-dropdown" data-bs-toggle="dropdown" aria-expanded="false"><span class="visually-hidden">Select Access Policy</span></button>',
    '<ul class="dropdown-menu ua-access-menu"></ul>',
    "</div>",
    '<div class="ua-access-inline text-muted small" data-access-inline></div>',
    "</div>"
  ].join("");

  var menu = wrapper.querySelector(".ua-access-menu");
  for (var i = 0; i < rows.length; i++) {
    var row = rows[i];
    var li = document.createElement("li");
    var btn = document.createElement("button");
    btn.type = "button";
    btn.className = "dropdown-item";
    btn.setAttribute("data-access-policy-item", row.policy);
    btn.innerHTML = '<i class="bi ' + row.icon + ' me-2"></i>' + row.title;
    li.appendChild(btn);
    menu.appendChild(li);
  }

  return wrapper;
}

function renderInlineInputs(wrapper) {
  var rows = policyOptions();
  var policy = normalizePolicy(wrapper.getAttribute("data-access-policy"));
  var option = findOption(rows, policy);
  var inline = wrapper.querySelector("[data-access-inline]");
  var pendingRoles = normalizeRoles(wrapper.getAttribute("data-access-pending-roles"));

  if (!(inline)) return;

  if (policy === POLICY_SHARED_SECRET) {
    inline.innerHTML = [
      '<span class="ua-access-help">' + option.text + "</span>",
      '<input type="password" class="form-control form-control-sm ua-access-inline-input" style="min-width:14rem;" data-access-shared-secret autocomplete="new-password" placeholder="Enter a shared secret">',
      '<span class="ua-access-inline-note text-muted">Stored as hash.</span>'
    ].join("");
    return;
  }

  if (policy === POLICY_ROLE) {
    inline.innerHTML = [
      '<span class="ua-access-help">When a user has one of these roles, they can view.</span>',
      '<div class="ua-access-email-editor d-flex flex-wrap align-items-center gap-2">',
      '<div class="ua-access-email-list d-flex flex-wrap gap-1" data-access-role-list></div>',
      '<input type="text" class="form-control form-control-sm ua-access-inline-input" style="min-width:14rem;" data-access-role-input placeholder="Add role and press Enter">',
      '<datalist data-access-role-options></datalist>',
      "</div>"
    ].join("");

    var roleInput = inline.querySelector("[data-access-role-input]");
    var roleList = inline.querySelector("[data-access-role-list]");
    var roleOptions = inline.querySelector("[data-access-role-options]");
    if (roleInput && roleOptions) {
      var listId = "ua-access-role-options-" + Math.random().toString(36).slice(2, 10);
      roleOptions.id = listId;
      roleInput.setAttribute("list", listId);
    }
    if (roleInput && roleList) {
      loadRoleOptions(wrapper);
      for (var rp = 0; rp < pendingRoles.length; rp++) createRoleChip(roleList, pendingRoles[rp]);

      roleInput.addEventListener("keydown", function(event) {
        if (event.key === "Enter" || event.key === ",") {
          event.preventDefault();
          var values = normalizeRoles(roleInput.value);
          for (var i = 0; i < values.length; i++) createRoleChip(roleList, values[i]);
          roleInput.value = "";
        }
      });

      roleInput.addEventListener("blur", function() {
        var values = normalizeRoles(roleInput.value);
        for (var i = 0; i < values.length; i++) createRoleChip(roleList, values[i]);
        roleInput.value = "";
      });

      roleInput.addEventListener("paste", function(event) {
        var text = "";
        if (event.clipboardData) text = event.clipboardData.getData("text/plain");
        var values = normalizeRoles(text);
        if (values.length > 1 || text.indexOf(",") >= 0 || text.indexOf("\n") >= 0 || text.indexOf(";") >= 0) {
          event.preventDefault();
          for (var i = 0; i < values.length; i++) createRoleChip(roleList, values[i]);
          roleInput.value = "";
        }
      });
    }
    return;
  }

  if (policy === POLICY_EMAIL) {
    inline.innerHTML = [
      '<span class="ua-access-help">' + option.text + "</span>",
      '<div class="ua-access-email-editor d-flex flex-wrap align-items-center gap-2">',
      '<div class="ua-access-email-list d-flex flex-wrap gap-1" data-access-email-list></div>',
      '<input type="text" class="form-control form-control-sm ua-access-inline-input" style="min-width:14rem;" data-access-email-input placeholder="Add email and press Enter">',
      "</div>"
    ].join("");

    var input = inline.querySelector("[data-access-email-input]");
    var list = inline.querySelector("[data-access-email-list]");
    if (input && list) {
      input.addEventListener("keydown", function(event) {
        if (event.key === "Enter" || event.key === ",") {
          event.preventDefault();
          var values = normalizeEmails(input.value);
          for (var i = 0; i < values.length; i++) createChip(list, values[i]);
          input.value = "";
        }
      });

      input.addEventListener("blur", function() {
        var values = normalizeEmails(input.value);
        for (var i = 0; i < values.length; i++) createChip(list, values[i]);
        input.value = "";
      });

      input.addEventListener("paste", function(event) {
        var text = "";
        if (event.clipboardData) {
          text = event.clipboardData.getData("text/plain");
        }
        var values = normalizeEmails(text);
        if (values.length > 1 || text.indexOf(",") >= 0 || text.indexOf("\n") >= 0 || text.indexOf(";") >= 0) {
          event.preventDefault();
          for (var i = 0; i < values.length; i++) createChip(list, values[i]);
          input.value = "";
        }
      });
    }
    return;
  }

  if (policy === POLICY_SECURE_LINK) {
    var maxExpiry = normalizeSecureLinkExpiry(wrapper.getAttribute("data-access-max-secure-link-expiry") || SECURE_LINK_NONE);
    var options = allowedSecureLinkExpiries(maxExpiry);
    var currentExpiry = clampSecureLinkExpiry(wrapper.getAttribute("data-access-secure-link-expiry") || SECURE_LINK_NONE, maxExpiry);

    if (options.length > 1) {
      inline.innerHTML = [
        '<span class="ua-access-help">' + option.text + "</span>",
        '<select class="form-select form-select-sm ua-access-inline-input" style="min-width:14rem;" data-access-secure-link-expiry>',
        options.map(function(item) {
          var selected = item === currentExpiry ? " selected" : "";
          var label = item === SECURE_LINK_24H ? "Expires in 24 hours"
            : item === SECURE_LINK_7D ? "Expires in 7 days"
            : item === SECURE_LINK_30D ? "Expires in 30 days"
            : "No expiration";
          return '<option value="' + item + '"' + selected + ">" + label + "</option>";
        }).join(""),
        "</select>"
      ].join("");
    } else {
      var only = options[0] || SECURE_LINK_24H;
      wrapper.setAttribute("data-access-secure-link-expiry", only);
      var text = only === SECURE_LINK_24H ? "Expires in 24 hours"
        : only === SECURE_LINK_7D ? "Expires in 7 days"
        : only === SECURE_LINK_30D ? "Expires in 30 days"
        : "No expiration";
      inline.innerHTML = '<span class="ua-access-help">' + option.text + " " + text + "</span>";
    }

    var secureExpirySelect = inline.querySelector("[data-access-secure-link-expiry]");
    if (secureExpirySelect) {
      secureExpirySelect.addEventListener("change", function() {
        wrapper.setAttribute("data-access-secure-link-expiry", clampSecureLinkExpiry(secureExpirySelect.value, maxExpiry));
      });
    }
    return;
  }

  inline.innerHTML = '<span class="ua-access-help">' + option.text + "</span>";
}

function updatePolicyUi(wrapper, policy) {
  var rows = policyOptions();
  var normalized = normalizePolicy(policy);
  var option = findOption(rows, normalized);
  var btn = wrapper.querySelector("[data-access-toggle='cycle']");
  var label = wrapper.querySelector(".ua-access-toggle-label");

  wrapper.setAttribute("data-access-policy", normalized);
  if (btn) {
    var icon = btn.querySelector("i");
    if (icon) icon.className = "bi " + option.icon + " me-1";
  }
  if (label) label.textContent = option.title;
  renderInlineInputs(wrapper);
}

function cyclePolicy(wrapper) {
  var rows = policyOptions();
  var current = normalizePolicy(wrapper.getAttribute("data-access-policy"));
  var index = 0;

  for (var i = 0; i < rows.length; i++) {
    if (rows[i].policy === current) {
      index = i;
      break;
    }
  }

  var next = rows[(index + 1) % rows.length];
  updatePolicyUi(wrapper, next.policy);
}

exports.render = async function(options) {
  var wrapper = buildMarkup(options || {});
  var currentPolicy = wrapper.getAttribute("data-access-policy");

  wrapper.querySelector("[data-access-toggle='cycle']").addEventListener("click", function() {
    cyclePolicy(wrapper);
  });

  wrapper.querySelectorAll("[data-access-policy-item]").forEach(function(item) {
    item.addEventListener("click", function() {
      updatePolicyUi(wrapper, item.getAttribute("data-access-policy-item"));
    });
  });

  updatePolicyUi(wrapper, currentPolicy);

  var preloadEmails = normalizeEmails(options && options.emails);
  if (preloadEmails.length > 0) {
    updatePolicyUi(wrapper, POLICY_EMAIL);
    var list = wrapper.querySelector("[data-access-email-list]");
    for (var i = 0; i < preloadEmails.length; i++) createChip(list, preloadEmails[i]);
  }

  return wrapper;
};

async function save(name, control, repo) {
  var policy = normalizePolicy(control.getAttribute("data-access-policy"));
  var errors = [];
  var data = {
    policy: policy,
    type: policy,
    role: "",
    requiredRole: "",
    accessRoles: [],
    emails: [],
    sharedSecret: "",
    sharedSecretHash: safe(control.getAttribute("data-access-existing-shared-secret-hash")),
    secureLinkExpiry: clampSecureLinkExpiry(control.getAttribute("data-access-secure-link-expiry"), control.getAttribute("data-access-max-secure-link-expiry"))
  };

  if (policy === POLICY_ROLE) {
    var roleList = control.querySelector("[data-access-role-list]");
    var roles = normalizeRoles(readRoles(roleList || control));
    if (roles.length < 1) {
      errors.push({
        type: "validation",
        message: "Add at least one role for role-based access."
      });
    } else {
      data.accessRoles = roles;
      data.requiredRole = roles[0];
      data.role = roles[0];
    }
  }

  var sharedSecretInput = control.querySelector("[data-access-shared-secret]");
  if (policy === POLICY_SHARED_SECRET) {
    data.sharedSecret = safe(sharedSecretInput ? sharedSecretInput.value : "");
    if (!(data.sharedSecret) && !(data.sharedSecretHash)) {
      errors.push({
        type: "validation",
        message: "Enter a shared secret."
      });
    }
  }

  var emailList = control.querySelector("[data-access-email-list]");
  if (policy === POLICY_EMAIL) {
    data.emails = normalizeEmails(readEmails(emailList || control));
    if (data.emails.length === 0) {
      errors.push({
        type: "validation",
        message: "Add at least one valid email address."
      });
    }
  }

  if (policy === POLICY_SECURE_LINK) {
    var secureLinkExpirySelect = control.querySelector("[data-access-secure-link-expiry]");
    var secureLinkExpiry = secureLinkExpirySelect ? secureLinkExpirySelect.value : control.getAttribute("data-access-secure-link-expiry");
    data.secureLinkExpiry = clampSecureLinkExpiry(secureLinkExpiry, control.getAttribute("data-access-max-secure-link-expiry"));
  }

  if (errors.length > 0) {
    repo.success = false;
    repo.errors = errors;
    control.classList.add("is-invalid");
    control.classList.remove("is-valid");
  } else {
    repo.success = true;
    repo.errors = [];
    control.classList.remove("is-invalid");
    control.classList.add("is-valid");
  }

  repo.data = data;
  return repo;
}
module.exports.save = save;

async function load(name, control, data) {
  var payload = (data && typeof data === "object") ? data : {};
  var policy = normalizePolicy(payload.policy || payload.type);
  var roles = normalizeRoles(payload.accessRoles || payload.roles || payload.role || payload.requiredRole || []);
  var sharedSecretHash = safe(payload.sharedSecretHash);
  var emails = normalizeEmails(payload.emails);
  var secureLinkExpiry = clampSecureLinkExpiry(payload.secureLinkExpiry || payload.secureLinkExpiresIn, control.getAttribute("data-access-max-secure-link-expiry"));

  if (sharedSecretHash) {
    control.setAttribute("data-access-existing-shared-secret-hash", sharedSecretHash);
  }

  control.setAttribute("data-access-pending-roles", roles.join(","));
  control.setAttribute("data-access-secure-link-expiry", secureLinkExpiry);
  updatePolicyUi(control, policy);

  if (policy === POLICY_EMAIL) {
    var list = control.querySelector("[data-access-email-list]");
    if (list) {
      list.innerHTML = "";
      for (var i = 0; i < emails.length; i++) createChip(list, emails[i]);
    }
  }
}
module.exports.load = load;
